import React, { FC } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'urql';

import ComposeMetaTopics from '../../../components/Compose/Common/ComposeMeta/ComposeMetaTopics';
import MetaTopic from '../../../components/MetaTopic';
import {
  TopicBasicFragment,
  TopicSettingsDocument,
} from '../../../generated/graphql';
import useToast from '../../../hooks/useToast';
import translate from '../../../i18n/translate';
import { ComposeModeParam } from '../../../models/pageParams';

import useComposeLinking from '../hooks/useComposeLinking';

import useComposeTopicPopulate from './hooks/useComposeTopicPopulate';
import { mapDispatchToProps, mapStateToProps } from './redux';
import { Props } from './types';

/**
 * Container for topics selection for templates
 *
 * @param props                            Props passed to the container
 * @param props.dataTopicCurrent           Currently selected topic
 * @param props.dataTopicSaved             Saved topic
 * @param props.dataTopicSearchValue       Value of search input
 * @param props.debugLoading               Whether to permanently enable loading state
 * @param props.isEditMode                 Whether Editor is in template edit mode
 * @param props.nodeType                   Whether the compose form is being used for messages or templates
 * @param props.requestTemplateTopicSearch Request update to topic search input
 * @param props.requestTopicChange         Request that the topic is changed
 * @param props.shouldResetAssignees       Whether assignees should be reset
 * @param props.shouldValidate             Whether the input should be validated
 * @returns                                The container itself
 */
const ComposeMetaTopicContainer: FC<Props> = ({
  dataTopicCurrent,
  dataTopicSaved,
  dataTopicSearchValue,
  debugLoading,
  nodeType,
  requestTemplateTopicSearch,
  requestTopicChange,
  shouldResetAssignees,
  shouldValidate,
}) => {
  const [{ data, fetching }] = useQuery({
    query: TopicSettingsDocument,
    variables: {},
  });
  const pushToast = useToast();

  const dataTopicAll = (data?.topicsAll ?? []) as TopicBasicFragment[];

  const mode: ComposeModeParam = 'topic';
  const { goBack, isActive, linkToggleProps } = useComposeLinking(mode);

  /**
   * Request change of the current topic
   *
   * @param topicId Id of the topic to select
   */
  const requestTopicSelect = (topicId: TopicBasicFragment['id']) => {
    if (topicId === dataTopicSaved) {
      return;
    }

    requestTopicChange(topicId);
    goBack();

    if (shouldResetAssignees) {
      pushToast({
        text: translate('ASSIGNEES__RESET_TOPIC_CHANGE'),
        type: 'warning',
      });
    }
  };

  useComposeTopicPopulate(
    dataTopicAll,
    requestTopicChange,
    dataTopicSaved,
    shouldResetAssignees,
  );

  const isLoading = fetching || debugLoading;

  return (
    <MetaTopic
      dataSearchValue={dataTopicSearchValue}
      dataTopicAll={dataTopicAll}
      dataTopicCurrent={dataTopicCurrent}
      dataTopicSaved={dataTopicSaved}
      inputCustom={
        <ComposeMetaTopics
          shouldValidate={shouldValidate}
          topic={dataTopicAll.find(topic => topic.id === dataTopicSaved)}
        />
      }
      isActive={isActive}
      isLoading={isLoading}
      linkToggleProps={linkToggleProps}
      nodeType={nodeType}
      onSearch={requestTemplateTopicSearch}
      requestTopicChange={requestTopicSelect}
      shouldValidate={shouldValidate}
    />
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ComposeMetaTopicContainer);
